import { useCallback, useRef } from "react";

import { useTracking } from "react-tracking";

import {
  BuiTabs,
  useTabsKeyboardNavigation,
} from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

import { useStickyHeight } from "../hooks/StickyContext";
import "./BTTabs.less";
import { type IBTTabPane, type IBTTabs } from "./BTTabs.types";

/**
 * Height (px) the sticky tab bar reserves in the sticky-header stack.
 * Moved from Clients.App's BTListLayoutHeader alongside BTTabs — BTTabs is the
 * only consumer of these two constants.
 */
const stickyHeaderHeightTabs = 56;
/** Level of the tab bar within the sticky-header stack (below the title header). */
const stickyHeaderLevelTabs = 20;

function useStickyTabBar(isSticky: boolean | number) {
  const enabled = !!isSticky;

  let level = 0;
  if (enabled) {
    level = stickyHeaderLevelTabs;
    if (typeof isSticky === "number") {
      level += isSticky;
    }
  }

  const top = useStickyHeight(
    enabled ? stickyHeaderHeightTabs : 0,
    "top",
    level,
  );

  return { enabled, top, height: stickyHeaderHeightTabs };
}

const BTTabsComponent = ({
  onChange,
  tabPosition = "top",
  "data-testid": testid,
  isSticky = false,
  fillContentHeight = false,
  className,
  tabBarStyle,
  ...otherProps
}: IBTTabs) => {
  const { trackEvent } = useTracking<Record<string, unknown>>({
    element: "Tabs",
    uniqueId: testid,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useTabsKeyboardNavigation({ containerRef, tabPosition });

  const sticky = useStickyTabBar(isSticky);

  const combinedClassName = cn("BTTabs", className, {
    "BTTabs--sticky": sticky.enabled,
    "BTTabs--fill-content-height": fillContentHeight,
  });

  const combinedTabBarStyle: React.CSSProperties | undefined = sticky.enabled
    ? { ...tabBarStyle, height: sticky.height, top: sticky.top }
    : tabBarStyle;

  // `exactOptionalPropertyTypes` forbids passing `tabBarStyle={undefined}`, so
  // only include it when there is a style to apply.
  const tabBarStyleProp = combinedTabBarStyle
    ? { tabBarStyle: combinedTabBarStyle }
    : {};

  const trackedOnChange = useCallback(
    (activeKey: string) => {
      trackEvent({
        event: "TabChange",
      });
      onChange?.(activeKey);
    },
    [onChange, trackEvent],
  );

  return (
    <BuiTabs
      ref={containerRef}
      tabPosition={tabPosition}
      testid={testid}
      {...otherProps}
      className={combinedClassName}
      {...tabBarStyleProp}
      onChange={trackedOnChange}
    />
  );
};

const BTTabPaneComponent = (props: IBTTabPane) => {
  return <BuiTabs.TabPane {...props} />;
};

/**
 * BTTabs - a composable tab navigation component with built-in accessibility.
 * Wraps `BuiTabs` from `@btnet/blueprint-components` with analytics tracking and
 * WAI-ARIA keyboard navigation (useTabsKeyboardNavigation) built in.
 *
 * Supports:
 * - Controlled and uncontrolled modes
 * - Multiple tab positions (top, bottom, left, right)
 * - Icons and custom content in tabs
 *
 * Keyboard navigation is automatic:
 * - Arrow Right/Left: Navigate tabs (horizontal)
 * - Arrow Down/Up: Navigate tabs (vertical)
 * - Home/End: Jump to first/last tab
 * - Enter: Activate focused tab
 * - Tab: Move focus into/out of tablist
 * @param props - The tabs component properties.
 * @param props.data-testid - Test ID for testing and analytics.
 * @param props.tabPosition - Position of tabs relative to pane content. Defaults to "top".
 * @param props.activeKey - Currently active tab key (controlled mode).
 * @param props.defaultActiveKey - Initial active tab key (uncontrolled mode).
 * @param props.onChange - Callback when active tab changes.
 * @example
 * ```tsx
 * <BTTabs data-testid="my-tabs" defaultActiveKey="1">
 *   <BTTabs.TabPane tab="Tab 1" key="1">
 *     Content 1
 *   </BTTabs.TabPane>
 *   <BTTabs.TabPane tab="Tab 2" key="2">
 *     Content 2
 *   </BTTabs.TabPane>
 * </BTTabs>
 * ```
 */
export const BTTabs = Object.assign(BTTabsComponent, {
  /**
   * BTTabs.TabPane sub-component for individual tab panels.
   * Accepts any ReactNode for maximum composability.
   * @param props - The tab pane properties.
   * @param props.tab - Tab label (accepts any ReactNode).
   * @param props.key - Unique key to identify this tab (required).
   * @param props.forceRender - Force render of tab pane content even when inactive. Defaults to false.
   * @param props.children - The content to render inside the tab pane.
   */
  TabPane: BTTabPaneComponent,
});
